/**
 * PublicAlertas — Funcionalidade 5 (Fase 3)
 *
 * Public follow-a-candidate list. The user provides an e-mail to identify
 * their subscription (no login required for MVP). Shows the candidates
 * they follow and the recent alerts generated by the daily diff job.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicAlertas() {
  const [email, setEmail] = useState(() => localStorage.getItem("todeolho:alerts-email") || "");
  const [loaded, setLoaded] = useState(false);
  const [following, setFollowing] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async (targetEmail) => {
    if (!targetEmail || !targetEmail.includes("@")) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/seguindo?email=${encodeURIComponent(targetEmail)}`);
      const j = await r.json();
      setFollowing(j.following || []);
      setAlerts(j.alerts || []);
      setLoaded(true);
      localStorage.setItem("todeolho:alerts-email", targetEmail);
    } catch {
      toast.error("Não conseguimos carregar seus alertas agora.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (email) carregar(email);
     
  }, []);

  const deixarDeSeguir = async (slug) => {
    try {
      await fetch(`${BACKEND}/api/public/candidatos/${slug}/seguir?email=${encodeURIComponent(email)}`, { method: "DELETE" });
      setFollowing((prev) => prev.filter((f) => f.slug !== slug));
      toast.success("Você não vai mais receber alertas sobre esse candidato.");
    } catch {
      toast.error("Erro ao remover. Tente novamente.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="alertas-page">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-[#F59E0B] mb-2">Alertas Personalizados</p>
        <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-1">Suas notificações</h1>
        <p className="text-sm text-slate-600">Acompanhe candidatos e receba um aviso por e-mail quando algo importante mudar.</p>
      </header>

      {!loaded && (
        <section className="bg-white rounded-2xl shadow-md p-6 mb-6" data-testid="alertas-email-gate">
          <label className="text-sm font-semibold text-slate-700 mb-2 block">Qual seu e-mail?</label>
          <p className="text-xs text-slate-500 mb-3">Usamos apenas para te avisar sobre os candidatos que você segue.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg min-h-[44px]"
              data-testid="alertas-email-input"
            />
            <button
              type="button"
              onClick={() => carregar(email)}
              disabled={loading || !email.includes("@")}
              className="bg-[#F59E0B] hover:bg-[#D97706] disabled:bg-slate-400 text-white font-semibold px-6 py-3 rounded-lg min-h-[44px]"
              data-testid="alertas-email-submit"
            >
              {loading ? "Carregando…" : "Ver meus alertas"}
            </button>
          </div>
        </section>
      )}

      {loaded && (
        <>
          <section className="bg-white rounded-2xl shadow-md p-6 mb-6" data-testid="alertas-following">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-800">Acompanhando ({following.length})</h2>
              <button
                type="button"
                onClick={() => { setLoaded(false); setEmail(""); localStorage.removeItem("todeolho:alerts-email"); }}
                className="text-xs text-slate-500 underline hover:text-slate-700"
                data-testid="alertas-change-email"
              >
                Trocar e-mail
              </button>
            </div>
            {following.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                Você ainda não acompanha nenhum candidato. Consulte um político e clique em <strong>Acompanhar</strong> para receber avisos.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {following.map((f) => (
                  <li key={f.slug} className="py-3 flex items-center justify-between">
                    <Link to={`/candidato/${f.slug}`} className="text-slate-800 hover:text-[#006030] font-medium" data-testid={`alertas-following-${f.slug}`}>
                      {f.candidato_nome}
                    </Link>
                    <button
                      type="button"
                      onClick={() => deixarDeSeguir(f.slug)}
                      className="text-xs text-slate-500 hover:text-red-600 underline"
                      data-testid={`alertas-unfollow-${f.slug}`}
                    >
                      Parar de seguir
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bg-white rounded-2xl shadow-md p-6" data-testid="alertas-history">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Novidades recentes</h2>
            {alerts.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                Nenhuma novidade por enquanto. Vamos te avisar aqui e por e-mail quando algo importante mudar.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {alerts.map((a, i) => (
                  <li key={i} className="py-3">
                    <p className="text-sm text-slate-800"><strong>{a.candidato_nome}</strong>: {a.mensagem}</p>
                    <p className="text-xs text-slate-500 mt-1">{a.created_at}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
